/**
 * AdsAnalytics Component
 * Admin dashboard for Rewarded Ads analytics
 * 
 * Features:
 * - Total rewards claimed
 * - Daily/Weekly/Monthly stats
 * - Country breakdown (Turkey & Azerbaijan highlighted)
 * - Daily trend chart
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdsAnalytics = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      const { data } = await axios.get(`${API_URL}/api/ads/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAnalytics(data);
      setLoading(false);
    } catch (err) {
      console.error('Analytics fetch error:', err);
      if (err.response?.status === 403) {
        setError('Admin yetkisi gerekli!');
      } else {
        setError(err.response?.data?.detail || 'Veriler yüklenemedi');
      }
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center', 
        background: 'linear-gradient(135deg, #0d0d2b 0%, #1a0533 100%)', 
        minHeight: '100vh', 
        color: 'white' 
      }}>
        <h2>📊 Yükleniyor...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center', 
        background: 'linear-gradient(135deg, #0d0d2b 0%, #1a0533 100%)', 
        minHeight: '100vh', 
        color: 'white' 
      }}>
        <h2>❌ Hata</h2>
        <p>{error}</p>
        <button 
          onClick={() => navigate('/admin')}
          style={{
            background: '#8b5cf6',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '20px'
          }}
        >
          ← Admin Panele Dön
        </button>
      </div>
    );
  }

  const { totals, today, week, month, highlighted_countries, country_breakdown, daily_trend, config, enabled } = analytics || {};

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, #0d0d2b 0%, #1a0533 100%)', 
      minHeight: '100vh', 
      padding: '40px',
      color: 'white'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <div>
            <h1 style={{ fontSize: '2rem', margin: '0 0 8px' }}>📺 Reklam Analytics</h1>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)' }}>
              Google AdMob Rewarded Ads Performansı
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={fetchAnalytics}
              style={{
                background: 'rgba(74,222,128,0.2)',
                color: '#4ade80',
                border: '1px solid rgba(74,222,128,0.3)',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              🔄 Yenile
            </button>
            <button 
              onClick={() => navigate('/admin')}
              style={{
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ← Admin Panel
            </button>
          </div>
        </div>

        {/* Status Badge */}
        <div style={{ 
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: enabled ? 'rgba(74,222,128,0.15)' : 'rgba(239,68,68,0.15)',
          border: `1px solid ${enabled ? 'rgba(74,222,128,0.3)' : 'rgba(239,68,68,0.3)'}`,
          padding: '8px 16px',
          borderRadius: '20px',
          marginBottom: '24px'
        }}>
          <span style={{ 
            width: '8px', 
            height: '8px', 
            borderRadius: '50%', 
            background: enabled ? '#4ade80' : '#ef4444' 
          }} />
          <span style={{ color: enabled ? '#4ade80' : '#ef4444', fontWeight: '500' }}>
            {enabled ? 'Aktif' : 'Devre Dışı'}
          </span>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
            • Günlük Limit: {config?.max_daily_rewards || 5} • Reklam Başı: {config?.credits_per_ad || 1} kredi
          </span>
        </div>

        {/* Main Stats Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
          gap: '16px', 
          marginBottom: '24px' 
        }}>
          {/* Total Rewards */}
          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px', 
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '4px' }}>📺</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#4ade80' }}>
              {totals?.total_rewards_claimed?.toLocaleString() || 0}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Toplam Reklam</div>
          </div>

          {/* Total Credits */}
          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px', 
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '4px' }}>💎</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#c084fc' }}>
              {totals?.total_credits_granted?.toLocaleString() || 0}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Verilen Kredi</div>
          </div>

          {/* Today's Stats */}
          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px', 
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '4px' }}>📅</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#fbbf24' }}>
              {today?.rewards_claimed?.toLocaleString() || 0}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Bugün</div>
          </div>

          {/* DAU for Ads */}
          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px', 
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '4px' }}>👥</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#60a5fa' }}>
              {today?.unique_users?.toLocaleString() || 0}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>DAU (Reklam)</div>
          </div>

          {/* Weekly */}
          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px', 
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '4px' }}>📊</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#f472b6' }}>
              {week?.rewards_claimed?.toLocaleString() || 0}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Son 7 Gün</div>
          </div>

          {/* Monthly */}
          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px', 
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '4px' }}>📈</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#a78bfa' }}>
              {month?.rewards_claimed?.toLocaleString() || 0}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>Son 30 Gün</div>
          </div>
        </div>

        {/* Highlighted Countries - Turkey & Azerbaijan */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '1.2rem', margin: '0 0 16px', color: 'rgba(255,255,255,0.9)' }}>
            🌍 Öne Çıkan Ülkeler
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            {/* Turkey */}
            <div style={{ 
              background: 'linear-gradient(135deg, rgba(239,68,68,0.1), rgba(239,68,68,0.05))',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: '16px', 
              padding: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <div style={{ fontSize: '48px' }}>🇹🇷</div>
              <div>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>
                  {highlighted_countries?.turkey?.country_name || 'Türkiye'}
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#ef4444' }}>
                  {highlighted_countries?.turkey?.count?.toLocaleString() || 0}
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                  {highlighted_countries?.turkey?.credits_granted?.toLocaleString() || 0} kredi verildi
                </div>
              </div>
            </div>

            {/* Azerbaijan */}
            <div style={{ 
              background: 'linear-gradient(135deg, rgba(59,130,246,0.1), rgba(59,130,246,0.05))',
              border: '1px solid rgba(59,130,246,0.3)',
              borderRadius: '16px', 
              padding: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <div style={{ fontSize: '48px' }}>🇦🇿</div>
              <div>
                <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>
                  {highlighted_countries?.azerbaijan?.country_name || 'Azerbaycan'}
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
                  {highlighted_countries?.azerbaijan?.count?.toLocaleString() || 0}
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                  {highlighted_countries?.azerbaijan?.credits_granted?.toLocaleString() || 0} kredi verildi
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Daily Trend */}
        <div style={{ 
          background: 'rgba(255,255,255,0.05)', 
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px', 
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h2 style={{ fontSize: '1.2rem', margin: '0 0 16px', color: 'rgba(255,255,255,0.9)' }}>
            📊 Son 7 Gün Trendi
          </h2>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '150px' }}>
            {daily_trend?.map((day, i) => {
              const maxRewards = Math.max(...(daily_trend?.map(d => d.rewards) || [1])) || 1;
              const height = (day.rewards / maxRewards) * 100;
              
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ 
                    width: '100%', 
                    background: 'linear-gradient(180deg, #4ade80, #22c55e)',
                    borderRadius: '4px 4px 0 0',
                    height: `${Math.max(height, 5)}%`,
                    minHeight: '4px',
                    transition: 'height 0.3s'
                  }} />
                  <div style={{ 
                    fontSize: '11px', 
                    color: 'rgba(255,255,255,0.5)', 
                    marginTop: '8px',
                    textAlign: 'center'
                  }}>
                    {new Date(day.date).toLocaleDateString('tr-TR', { weekday: 'short' })}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#4ade80', 
                    fontWeight: '600'
                  }}>
                    {day.rewards}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Country Breakdown Table */}
        <div style={{ 
          background: 'rgba(255,255,255,0.05)', 
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px', 
          padding: '24px'
        }}>
          <h2 style={{ fontSize: '1.2rem', margin: '0 0 16px', color: 'rgba(255,255,255,0.9)' }}>
            🌐 Ülke Kırılımı (Top 20)
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: 'rgba(255,255,255,0.6)', fontWeight: '500' }}>Ülke</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255,255,255,0.6)', fontWeight: '500' }}>Reklam Sayısı</th>
                  <th style={{ padding: '12px', textAlign: 'right', color: 'rgba(255,255,255,0.6)', fontWeight: '500' }}>Verilen Kredi</th>
                </tr>
              </thead>
              <tbody>
                {country_breakdown?.length > 0 ? (
                  country_breakdown.map((country, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                      <td style={{ padding: '12px', color: 'white' }}>
                        {country.country === 'TR' && '🇹🇷 '}
                        {country.country === 'AZ' && '🇦🇿 '}
                        {country.country === 'unknown' ? 'Bilinmiyor' : country.country}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#4ade80', fontWeight: '600' }}>
                        {country.count?.toLocaleString() || 0}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'right', color: '#c084fc' }}>
                        {country.credits_granted?.toLocaleString() || 0}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={{ padding: '24px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                      Henüz veri yok
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Last Updated */}
        <div style={{ 
          marginTop: '24px', 
          textAlign: 'center', 
          color: 'rgba(255,255,255,0.4)',
          fontSize: '13px'
        }}>
          Son güncelleme: {analytics?.last_updated ? new Date(analytics.last_updated).toLocaleString('tr-TR') : '-'}
        </div>
      </div>
    </div>
  );
};

export default AdsAnalytics;
